import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../contexts/GameContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { StatsGrid } from "../components/game/StatsDisplay";
import { ELEMENTS } from "../utils/gameLogic";
import { SFX, playMusic } from "../utils/audio";
import { ArrowLeft, ChevronRight, Sparkles, Lock } from "lucide-react";
import { CLASS_UNLOCK_LEVEL, PURCHASE_ONLY_CLASSES, isClassUnlocked } from "../data/profile";

const ClassSelect = () => {
  const [selected, setSelected] = useState(null);
  const { startNewRun } = useGame();
  const { profile } = useProfile();
  const navigate = useNavigate();

  React.useEffect(() => { playMusic("select"); }, []);

  const classes = Object.values(CLASSES);
  const selectedClass = selected ? CLASSES[selected] : null;
  const playerLevel = profile?.level || 1;
  const owned = profile?.cosmetics_owned || [];
  const selectedUnlocked = selected ? isClassUnlocked(selected, playerLevel, owned) : false;

  const handleConfirm = () => {
    if (!selected || !selectedUnlocked) return;
    SFX.click();
    startNewRun(selected);
    navigate("/map");
  };

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b-4 border-slate-950 bg-slate-800">
        <button
          data-testid="back-to-menu"
          onClick={() => navigate("/")}
          className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Menu
        </button>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Choisis ta classe</h1>
        <div data-testid="player-level-badge" className="bg-fuchsia-500 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-display font-bold text-sm flex items-center gap-1">
          <Sparkles className="w-4 h-4" /> Niv. {playerLevel}
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-[1fr_400px] overflow-hidden">
        {/* Class grid */}
        <div className="overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {classes.map((c) => {
              const isSelected = selected === c.id;
              const unlockLvl = CLASS_UNLOCK_LEVEL[c.id] ?? 1;
              const purchase = PURCHASE_ONLY_CLASSES[c.id];
              const locked = !isClassUnlocked(c.id, playerLevel, owned);
              return (
                <button
                  key={c.id}
                  data-testid={`class-card-${c.id}`}
                  onClick={() => { SFX.hover(); setSelected(c.id); }}
                  className={`relative text-left bg-slate-800 border-4 ${isSelected ? "border-yellow-400" : "border-slate-950"} rounded-2xl p-3 cartoon-shadow-lg-static transition-transform hover:-translate-y-1 ${locked ? "opacity-70" : ""}`}
                >
                  <div className={`w-full aspect-square rounded-xl bg-gradient-to-br ${c.couleur} border-2 border-slate-950 overflow-hidden mb-2 relative`}>
                    <img src={c.image} alt={c.nom} className={`w-full h-full object-cover ${locked ? "grayscale brightness-50" : ""}`} />
                    {locked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/60">
                        <Lock className="w-10 h-10 text-yellow-300 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]" />
                        <span className="mt-1 font-display font-bold text-yellow-300 text-sm" data-testid={`class-lock-${c.id}`}>{purchase ? `🪙 ${purchase.cost.toLocaleString("fr-FR")}` : `Niv. ${unlockLvl}`}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-display text-base font-bold leading-tight">{c.nom}</h3>
                  {isSelected && (
                    <div className="absolute -top-2 -right-2 bg-yellow-400 text-slate-950 border-4 border-slate-950 rounded-full w-9 h-9 flex items-center justify-center cartoon-shadow-static">
                      <Sparkles className="w-4 h-4" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-slate-800 border-t-4 lg:border-t-0 lg:border-l-4 border-slate-950 p-4 sm:p-6 flex flex-col overflow-y-auto">
          {!selectedClass ? (
            <div className="flex-1 flex items-center justify-center text-center text-slate-500">
              <div>
                <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-display text-lg">Sélectionne une classe pour voir ses détails</p>
              </div>
            </div>
          ) : (
            <>
              <div className={`w-full aspect-square max-h-64 rounded-2xl bg-gradient-to-br ${selectedClass.couleur} border-4 border-slate-950 overflow-hidden mb-4 cartoon-shadow-lg-static relative`}>
                <img src={selectedClass.image} alt={selectedClass.nom} className={`w-full h-full object-cover ${!selectedUnlocked ? "grayscale brightness-50" : ""}`} />
                {!selectedUnlocked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/60">
                    <Lock className="w-16 h-16 text-yellow-300 drop-shadow-[3px_3px_0_rgba(0,0,0,1)]" />
                    <span className="mt-2 font-display font-bold text-yellow-300 text-xl">Verrouillée</span>
                    {PURCHASE_ONLY_CLASSES[selectedClass.id] ? (
                      <span className="text-yellow-200 text-sm font-bold text-center px-2">Débloque-la en Boutique pour 🪙 {PURCHASE_ONLY_CLASSES[selectedClass.id].cost.toLocaleString("fr-FR")} or</span>
                    ) : (
                      <span className="text-yellow-200 text-sm font-bold">Atteins le niveau {CLASS_UNLOCK_LEVEL[selectedClass.id]}</span>
                    )}
                  </div>
                )}
              </div>
              <h2 data-testid="class-detail-name" className="font-display text-2xl font-bold mb-1">{selectedClass.nom}</h2>
              {selectedClass.element && (
                <span data-testid="class-element-badge" className={`inline-block ${ELEMENTS[selectedClass.element].couleur} ${ELEMENTS[selectedClass.element].textColor} border-2 border-slate-950 rounded-md px-2 py-0.5 text-xs font-bold mb-2`}>
                  {ELEMENTS[selectedClass.element].icon} Affinité : {ELEMENTS[selectedClass.element].nom}
                </span>
              )}
              <p className="text-sm text-slate-300 mb-4">{selectedClass.description}</p>

              <h3 className="font-display text-xs uppercase tracking-wider text-slate-400 mb-2">Stats de Base</h3>
              <div className="mb-4">
                <StatsGrid stats={{ ...selectedClass.baseStats, pvMax: selectedClass.baseStats.pv }} compact />
              </div>

              <div className="bg-slate-900 border-2 border-slate-700 rounded-xl p-3 mb-4">
                <h3 className="font-display text-xs uppercase tracking-wider text-yellow-400 mb-2">
                  Compétences ({selectedClass.competences.length})
                </h3>
                <div className="space-y-1.5">
                  {selectedClass.competences.map((c) => (
                    <div key={c.id} className="flex items-start gap-2 text-xs">
                      <span className="text-base">{c.icon}</span>
                      <div className="flex-1">
                        <p className="font-bold text-slate-100">{c.nom} <span className="text-sky-400">({c.cout === 0 ? "Gratuit" : `${c.cout}⚡`})</span></p>
                        <p className="text-slate-400 leading-tight">{c.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                data-testid="confirm-class-btn"
                onClick={handleConfirm}
                disabled={!selectedUnlocked}
                className={`mt-auto font-display font-bold uppercase tracking-wider py-3 px-4 rounded-2xl border-4 border-slate-950 cartoon-shadow flex items-center justify-center gap-2 ${
                  selectedUnlocked ? "bg-yellow-400 text-slate-950" : "bg-slate-700 text-slate-400 cursor-not-allowed"
                }`}
              >
                {selectedUnlocked ? (
                  <>Commencer l'aventure <ChevronRight className="w-5 h-5" /></>
                ) : PURCHASE_ONLY_CLASSES[selectedClass.id] ? (
                  <><Lock className="w-4 h-4" /> À acheter en Boutique ({PURCHASE_ONLY_CLASSES[selectedClass.id].cost.toLocaleString("fr-FR")} or)</>
                ) : (
                  <><Lock className="w-4 h-4" /> Niveau {CLASS_UNLOCK_LEVEL[selectedClass.id]} requis</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassSelect;
